import { ParseKeys, TFunction } from "i18next"
import { useTranslation } from "react-i18next"

import {
  OtherInitiativeCertificate,
  ScopeCertificate,
  TransactionCertificate,
} from "~/pages/Brand/Certificates/components/templates.ts"

type ScopeCertificateKeys = keyof ScopeCertificate
type TransactionCertificateKeys = keyof TransactionCertificate
type OtherInitiativeCertificateKeys = keyof OtherInitiativeCertificate

type FilteredCertificateKeys =
  | ScopeCertificateKeys
  | TransactionCertificateKeys
  | OtherInitiativeCertificateKeys

type TranslationValue = ParseKeys<"certificates">

type TranslationIndexerCertificate = {
  [key in FilteredCertificateKeys]: TranslationValue
}

type TranslationIndexerStandard = {
  relevantStandard: TranslationValue
  relevantStandardDesc: TranslationValue
  validityPeriod: TranslationValue
  validityPeriodDesc: TranslationValue
  certifiedEntity: TranslationValue
  certifiedEntityDesc: TranslationValue
  productCategories: TranslationValue
  productCategoriesDesc: TranslationValue
  certificationBody: TranslationValue
  certificationBodyDesc: TranslationValue
}

export const placeHolderTranslations: TranslationIndexerCertificate = {
  buyerOfCertifiedProducts: "modal.placeholders.buyerOfCertifiedProducts",
  certificationBody: "modal.placeholders.certificationBody",
  certifiedOrganizationLicenseNumber:
    "modal.placeholders.certifiedOrganizationLicenseNumber",
  certifiedOrganizationName: "modal.placeholders.certifiedOrganizationName",
  certifiedWeight: "modal.placeholders.certifiedWeight",
  expiryDate: "modal.placeholders.expiryDate",
  issueDate: "modal.placeholders.issueDate",
  linkedScopeCertificateNumber:
    "modal.placeholders.linkedScopeCertificateNumber",
  name: "modal.placeholders.name",
  processingSteps: "modal.placeholders.processingSteps",
  productCategories: "modal.placeholders.productCategories",
  reference: "modal.placeholders.reference",
  score: "modal.placeholders.score",
  typeId: "modal.placeholders.type",
  certifiedOrganizationAddress:
    "modal.placeholders.certifiedOrganizationAddress",
  productDetails: "modal.placeholders.productDetails",
}

export const labelHolderTranslations: TranslationIndexerCertificate = {
  buyerOfCertifiedProducts: "modal.labels.buyerOfCertifiedProducts",
  certificationBody: "modal.labels.certificationBody",
  certifiedOrganizationLicenseNumber:
    "modal.labels.certifiedOrganizationLicenseNumber",
  certifiedOrganizationName: "modal.labels.certifiedOrganizationName",
  certifiedWeight: "modal.labels.certifiedWeight",
  expiryDate: "modal.labels.expiryDate",
  issueDate: "modal.labels.issueDate",
  linkedScopeCertificateNumber: "modal.labels.linkedScopeCertificateNumber",
  name: "modal.labels.name",
  processingSteps: "modal.labels.processingSteps",
  productCategories: "modal.labels.productCategories",
  reference: "modal.labels.reference",
  score: "modal.labels.score",
  typeId: "modal.labels.type",
  certifiedOrganizationAddress: "modal.labels.certifiedOrganizationAddress",
  productDetails: "modal.labels.productDetails",
}

export const standardRulesTranslations: TranslationIndexerStandard = {
  relevantStandard: "standard.drawer.rules.relevantStandard",
  relevantStandardDesc: "standard.drawer.rules.relevantStandardDesc",
  validityPeriod: "standard.drawer.rules.validityPeriod",
  validityPeriodDesc: "standard.drawer.rules.validityPeriodDesc",
  certifiedEntity: "standard.drawer.rules.certifiedEntity",
  certifiedEntityDesc: "standard.drawer.rules.certifiedEntityDesc",
  productCategories: "standard.drawer.rules.productCategories",
  productCategoriesDesc: "standard.drawer.rules.productCategoriesDesc",
  certificationBody: "standard.drawer.rules.certificationBody",
  certificationBodyDesc: "standard.drawer.rules.certificationBodyDesc",
}

export type CertificateScore =
  | "notApplicable"
  | "other"
  | "lwgPlatinum"
  | "lwgGold"
  | "lwgSilver"
  | "lwgBronze"
  | "lwgOther"
  | "lwgAudited"
  | "lwgCertified"
  | "bsciA"
  | "bsciB"
  | "bsciC"
  | "bsciD"
  | "bsciE"
  | "bsciOther"
  | "icsA"
  | "icsB"
  | "icsC"
  | "icsD"
  | "icsE"
  | "icsOther"
  | "sa8000Compliant"
  | "sa8000PartlyMinor"
  | "sa8000PartlyMajor"
  | "sa8000NonCompliant"
  | "sa8000Other"
  | "bepiLeader"
  | "bepiAdvanced"
  | "bepiProgressing"
  | "bepiBeginner"
  | "bepiInsufficient"
  | "bepiOther"
  | "wrapPlatinum"
  | "wrapGold"
  | "wrapSilver"
  | "wrapBronze"
  | "wrapOther"
  | "wrapCertified"
  | "gotsCertified"
  | "gotsInConversion"
  | "gotsSuspended"
  | "gotsWithdrawn"
  | "gotsOther"
  | "oekotexStandard100"
  | "oekotexStepLevel1"
  | "oekotexStepLevel2"
  | "oekotexStepLevel3"
  | "oekotexMadeInGreen"
  | "oekotexLeatherStandard"
  | "oekotexEcoPassport"
  | "oekotexOther"
  | "smeta2Pillar"
  | "smeta4Pillar"
  | "smetaNoNonCompliance"
  | "smetaMinorNonCompliance"
  | "smetaMajorNonCompliance"
  | "smetaCriticalNonCompliance"
  | "smetaOther"
  | "higgFemBeginner"
  | "higgFemDeveloping"
  | "higgFemEstablished"
  | "higgFemLeading"
  | "higgFemOther"
  | "rcsCertified"
  | "rcsInConversion"
  | "rcsSuspended"
  | "rcsWithdrawn"
  | "rcsOther"
  | "grsCertified"
  | "grsInConversion"
  | "grsSuspended"
  | "grsWithdrawn"
  | "grsOther"
  | "iso14001Certified"
  | "iso14001NotCertified"
  | "iso14001Suspended"
  | "iso14001Withdrawn"
  | "iso14001Other"
  | "iso45001Certified"
  | "iso45001NotCertified"
  | "iso45001Suspended"
  | "iso45001Withdrawn"
  | "iso45001Other"

export const getScoreTranslation = (
  score: CertificateScore,
  t: TFunction<"certificates">
) => {
  return getScoreTranslations(t)[score]
}

export const getScoreTranslations = (
  t: TFunction<"certificates">
): {
  [key in CertificateScore]: string
} => {
  const scoreTranslations: {
    [key in CertificateScore]: string
  } = {
    notApplicable: t("modal.labels.scoreOptions.notApplicable"),
    other: t("modal.labels.scoreOptions.other"),
    lwgPlatinum: t("modal.labels.scoreOptions.lwg.platinum"),
    lwgGold: t("modal.labels.scoreOptions.lwg.gold"),
    lwgSilver: t("modal.labels.scoreOptions.lwg.silver"),
    lwgBronze: t("modal.labels.scoreOptions.lwg.bronze"),
    lwgOther: t("modal.labels.scoreOptions.lwg.other"),
    lwgAudited: t("modal.labels.scoreOptions.lwg.audited"),
    lwgCertified: t("modal.labels.scoreOptions.lwg.certified"),
    bsciA: t("modal.labels.scoreOptions.bsci.a"),
    bsciB: t("modal.labels.scoreOptions.bsci.b"),
    bsciC: t("modal.labels.scoreOptions.bsci.c"),
    bsciD: t("modal.labels.scoreOptions.bsci.d"),
    bsciE: t("modal.labels.scoreOptions.bsci.e"),
    bsciOther: t("modal.labels.scoreOptions.bsci.other"),

    icsA: t("modal.labels.scoreOptions.ics.a"),
    icsB: t("modal.labels.scoreOptions.ics.b"),
    icsC: t("modal.labels.scoreOptions.ics.c"),
    icsD: t("modal.labels.scoreOptions.ics.d"),
    icsE: t("modal.labels.scoreOptions.ics.e"),
    icsOther: t("modal.labels.scoreOptions.ics.other"),

    sa8000Compliant: t("modal.labels.scoreOptions.sa8000.compliant"),
    sa8000PartlyMinor: t("modal.labels.scoreOptions.sa8000.partlyMinor"),
    sa8000PartlyMajor: t("modal.labels.scoreOptions.sa8000.partlyMajor"),
    sa8000NonCompliant: t("modal.labels.scoreOptions.sa8000.nonCompliant"),
    sa8000Other: t("modal.labels.scoreOptions.sa8000.other"),

    bepiLeader: t("modal.labels.scoreOptions.bepi.leader"),
    bepiAdvanced: t("modal.labels.scoreOptions.bepi.advanced"),
    bepiProgressing: t("modal.labels.scoreOptions.bepi.progressing"),
    bepiBeginner: t("modal.labels.scoreOptions.bepi.beginner"),
    bepiInsufficient: t("modal.labels.scoreOptions.bepi.insufficient"),
    bepiOther: t("modal.labels.scoreOptions.bepi.other"),

    wrapPlatinum: t("modal.labels.scoreOptions.wrap.platinum"),
    wrapGold: t("modal.labels.scoreOptions.wrap.gold"),
    wrapSilver: t("modal.labels.scoreOptions.wrap.silver"),
    wrapBronze: t("modal.labels.scoreOptions.wrap.bronze"),
    wrapCertified: t("modal.labels.scoreOptions.wrap.certified"),
    wrapOther: t("modal.labels.scoreOptions.wrap.other"),

    gotsCertified: t("modal.labels.scoreOptions.gots.certified"),
    gotsInConversion: t("modal.labels.scoreOptions.gots.inConversion"),
    gotsSuspended: t("modal.labels.scoreOptions.gots.suspended"),
    gotsWithdrawn: t("modal.labels.scoreOptions.gots.withdrawn"),
    gotsOther: t("modal.labels.scoreOptions.gots.other"),

    oekotexStandard100: t("modal.labels.scoreOptions.oekotex.standard100"),
    oekotexStepLevel1: t("modal.labels.scoreOptions.oekotex.stepLevel1"),
    oekotexStepLevel2: t("modal.labels.scoreOptions.oekotex.stepLevel2"),
    oekotexStepLevel3: t("modal.labels.scoreOptions.oekotex.stepLevel3"),
    oekotexMadeInGreen: t("modal.labels.scoreOptions.oekotex.madeInGreen"),
    oekotexLeatherStandard: t(
      "modal.labels.scoreOptions.oekotex.leatherStandard"
    ),
    oekotexEcoPassport: t("modal.labels.scoreOptions.oekotex.ecoPassport"),
    oekotexOther: t("modal.labels.scoreOptions.oekotex.other"),

    smeta2Pillar: t("modal.labels.scoreOptions.smeta.twoPillar"),
    smeta4Pillar: t("modal.labels.scoreOptions.smeta.fourPillar"),
    smetaNoNonCompliance: t("modal.labels.scoreOptions.smeta.noNonCompliance"),
    smetaMinorNonCompliance: t(
      "modal.labels.scoreOptions.smeta.minorNonCompliance"
    ),
    smetaMajorNonCompliance: t(
      "modal.labels.scoreOptions.smeta.majorNonCompliance"
    ),
    smetaCriticalNonCompliance: t(
      "modal.labels.scoreOptions.smeta.criticalNonCompliance"
    ),
    smetaOther: t("modal.labels.scoreOptions.smeta.other"),

    higgFemBeginner: t("modal.labels.scoreOptions.higgFem.beginner"),
    higgFemDeveloping: t("modal.labels.scoreOptions.higgFem.developing"),
    higgFemEstablished: t("modal.labels.scoreOptions.higgFem.established"),
    higgFemLeading: t("modal.labels.scoreOptions.higgFem.leading"),
    higgFemOther: t("modal.labels.scoreOptions.higgFem.other"),

    rcsCertified: t("modal.labels.scoreOptions.rcs.certified"),
    rcsInConversion: t("modal.labels.scoreOptions.rcs.inConversion"),
    rcsSuspended: t("modal.labels.scoreOptions.rcs.suspended"),
    rcsWithdrawn: t("modal.labels.scoreOptions.rcs.withdrawn"),
    rcsOther: t("modal.labels.scoreOptions.rcs.other"),

    grsCertified: t("modal.labels.scoreOptions.grs.certified"),
    grsInConversion: t("modal.labels.scoreOptions.grs.inConversion"),
    grsSuspended: t("modal.labels.scoreOptions.grs.suspended"),
    grsWithdrawn: t("modal.labels.scoreOptions.grs.withdrawn"),
    grsOther: t("modal.labels.scoreOptions.grs.other"),

    iso14001Certified: t("modal.labels.scoreOptions.iso14001.certified"),
    iso14001NotCertified: t("modal.labels.scoreOptions.iso14001.notCertified"),
    iso14001Suspended: t("modal.labels.scoreOptions.iso14001.suspended"),
    iso14001Withdrawn: t("modal.labels.scoreOptions.iso14001.withdrawn"),
    iso14001Other: t("modal.labels.scoreOptions.iso14001.other"),

    iso45001Certified: t("modal.labels.scoreOptions.iso45001.certified"),
    iso45001NotCertified: t("modal.labels.scoreOptions.iso45001.notCertified"),
    iso45001Suspended: t("modal.labels.scoreOptions.iso45001.suspended"),
    iso45001Withdrawn: t("modal.labels.scoreOptions.iso45001.withdrawn"),
    iso45001Other: t("modal.labels.scoreOptions.iso45001.other"),
  }

  return scoreTranslations
}

type TooltipTranslationIndexerCertificate = {
  [key in keyof Pick<TranslationIndexerCertificate, "score">]: string
}

export const getTooltipTranslation = (
  tooltipKey: keyof TranslationIndexerCertificate,
  t: TFunction<"certificates">
) => {
  const tooltipTranslation = getTooltipTranslations(t)
  if (!(tooltipKey in tooltipTranslation)) {
    return undefined
  }

  return tooltipTranslation[
    tooltipKey as keyof TooltipTranslationIndexerCertificate
  ]
}

export const getTooltipTranslations = (
  t: TFunction<"certificates">
): TooltipTranslationIndexerCertificate => {
  const tooltipTranslations: TooltipTranslationIndexerCertificate = {
    score: t("modal.labels.scoreTooltip"),
  }

  return tooltipTranslations
}

export function useReacti18Translation() {
  const { t } = useTranslation("certificates")

  // t("modal.placeholders.buyerOfCertifiedProducts")
  // t("modal.placeholders.certificationBody")
  // t("modal.placeholders.certifiedOrganizationLicenseNumber")
  // t("modal.placeholders.certifiedOrganizationName")
  // t("modal.placeholders.certifiedWeight")
  // t("modal.placeholders.expiryDate")
  // t("modal.placeholders.issueDate")
  // t("modal.placeholders.linkedScopeCertificateNumber")
  // t("modal.placeholders.name")
  // t("modal.placeholders.processingSteps")
  // t("modal.placeholders.productCategories")
  // t("modal.placeholders.reference")
  // t("modal.placeholders.rejectionMessage")
  // t("modal.placeholders.score")
  // t("modal.placeholders.status")
  // t("modal.placeholders.type")
  // t("modal.placeholders.certifiedOrganizationAddress")
  // t("modal.placeholders.productDetails")

  // t("modal.labels.buyerOfCertifiedProducts")
  // t("modal.labels.certificationBody")
  // t("modal.labels.certifiedOrganizationLicenseNumber")
  // t("modal.labels.certifiedOrganizationName")
  // t("modal.labels.certifiedWeight")
  // t("modal.labels.expiryDate")
  // t("modal.labels.issueDate")
  // t("modal.labels.linkedScopeCertificateNumber")
  // t("modal.labels.name")
  // t("modal.labels.processingSteps")
  // t("modal.labels.productCategories")
  // t("modal.labels.reference")
  // t("modal.labels.rejectionMessage")
  // t("modal.labels.score")
  // t("modal.labels.status")
  // t("modal.labels.type")
  // t("modal.labels.certifiedOrganizationAddress")
  // t("modal.labels.productDetails")

  // t("standard.drawer.rules.relevantStandard")
  // t("standard.drawer.rules.relevantStandardDesc")
  // t("standard.drawer.rules.validityPeriod")
  // t("standard.drawer.rules.validityPeriodDesc")
  // t("standard.drawer.rules.certifiedEntity")
  // t("standard.drawer.rules.certifiedEntityDesc")
  // t("standard.drawer.rules.productCategories")
  // t("standard.drawer.rules.productCategoriesDesc")
  // t("standard.drawer.rules.certificationBody")
  // t("standard.drawer.rules.certificationBodyDesc")

  void t
}
